/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { createTheme } from '../createTheme';
import {
  colors as colorsDark,
  elevations as elevationsDark,
} from './dark/darkTheme';
import {
  colors as colorsDemo,
  elevations as elevationsDemo,
} from './demo/demoTheme';
import {
  colors as colorsEumetnet,
  elevations as elevationsEumetnet,
} from './eumetnet/eumetnetTheme';
import {
  colors as colorsLight,
  elevations as elevationsLight,
} from './light/lightTheme';
import {
  colors as colorsXmas,
  elevations as elevationsXmas,
} from './xmas/xmasTheme';
import type { NamedTheme } from '../../types';
import { ThemeTypes } from '../../types';

export const getThemeByName = (themeName: ThemeTypes): NamedTheme => {
  switch (themeName) {
    case ThemeTypes.DARK_THEME:
      return namedDarkTheme;
    case ThemeTypes.LIGHT_THEME:
      return namedLightTheme;
    case ThemeTypes.XMAS_THEME:
      return namedXmasTheme;
    case ThemeTypes.DEMO_THEME:
      return namedDemoTheme;
    case ThemeTypes.EUMETNET_THEME:
      return namedEumetnetTheme;
    default:
      return namedLightTheme;
  }
};

export const lightTheme = createTheme({
  paletteType: 'light',
  geowebColors: colorsLight,
  elevations: elevationsLight,
});

export const darkTheme = createTheme({
  paletteType: 'dark',
  geowebColors: colorsDark,
  elevations: elevationsDark,
});

export const xmasTheme = createTheme({
  paletteType: 'dark',
  geowebColors: colorsXmas,
  elevations: elevationsXmas,
});

export const demoTheme = createTheme({
  paletteType: 'light',
  geowebColors: colorsDemo,
  elevations: elevationsDemo,
});

export const eumetnetTheme = createTheme({
  paletteType: 'light',
  geowebColors: colorsEumetnet,
  elevations: elevationsEumetnet,
});

export const namedLightTheme = {
  name: ThemeTypes.LIGHT_THEME,
  theme: lightTheme,
};

export const namedDarkTheme = {
  name: ThemeTypes.DARK_THEME,
  theme: darkTheme,
};

export const namedXmasTheme = {
  name: ThemeTypes.XMAS_THEME,
  theme: xmasTheme,
};

export const namedDemoTheme = {
  name: ThemeTypes.DEMO_THEME,
  theme: demoTheme,
};

export const namedEumetnetTheme = {
  name: ThemeTypes.EUMETNET_THEME,
  theme: eumetnetTheme,
};

export { colorsLight, elevationsLight };
